import type { SegmentDto } from '@carbonwebmail/contracts'
import { EllipsisMenu } from '../../ui/EllipsisMenu.js'
import { MenuItem, MenuRule } from '../../ui/Menu.js'

/**
 * What can be done to one segment, from its own row.
 *
 * The same `⋯` the contacts board puts on a column heading, and for the same
 * reason: the place somebody looks for "what can I do to this" is the thing
 * itself, not a column of links at the end of the row. Four links per row was
 * what this screen had, and a table whose last column is a row of verbs is one
 * where the verbs are what you read instead of the data.
 *
 * **Renaming happens in place.** Opening the rule builder to change a name is a
 * screen away from a list somebody is scanning, and the builder replaces the
 * segment whole — so the rename that goes through it is carrying the rules
 * along for the ride whether or not anybody meant to touch them.
 *
 * **Deleting says what it would erase.** It is refused while a campaign was
 * sent to this segment, because that campaign's audience is part of the record
 * of what happened — and the refusal arrives as a sentence from the server
 * rather than as a disabled row here, because only the server knows.
 */
export function SegmentMenu({
  segment,
  onEdit,
  onExport,
  onImport,
  onDelete,
}: {
  readonly segment: SegmentDto
  readonly onEdit: () => void
  readonly onExport: () => void
  readonly onImport: () => void
  readonly onDelete: () => void
}) {
  return (
    <EllipsisMenu hover="row" label={`Options for ${segment.name}`} width="w-56">
      {(choose) => (
        <>
          <MenuItem onClick={choose(onEdit)}>Edit name and columns</MenuItem>
          <MenuItem onClick={choose(onExport)}>Export who it reaches</MenuItem>
          {/*
            Importing belongs to the segment somebody is looking at, not to a screen
            where they have to remember which one they meant. The import connects
            every row it inserts, in the same transaction as the contact.
          */}
          <MenuItem onClick={choose(onImport)}>Import contacts into it</MenuItem>
          <MenuRule />
          <MenuItem danger onClick={choose(onDelete)}>
            Delete segment
          </MenuItem>
        </>
      )}
    </EllipsisMenu>
  )
}
